import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { CheckCircleIcon } from '@heroicons/react/24/outline'
import Button from '../../components/common/Button'
import { RootState } from '../../store/store'
import { couponsApi, paymentsApi, subscriptionsApi } from '../../services/api'
import { SubscriptionPlan } from '../../types'
import {
  clearSelectedCheckoutPlan,
  getBillingCycleLabel,
  getPlanFeatureList,
  getSelectedCheckoutPlanRef,
  saveSelectedCheckoutPlan,
} from '../../utils/checkoutPlan'
import { validateEmail, validateFullName, validatePhone } from '../../utils/validation'
import { evaluatePurchaseAccess } from '../../utils/accessFlow'
import RichTextContent from '../../components/common/RichTextContent'

declare global {
  interface Window {
    Razorpay: any
  }
}

function loadRazorpayScript(): Promise<boolean> {
  return new Promise(resolve => {
    if (window.Razorpay) {
      resolve(true)
      return
    }
    const script = document.createElement('script')
    script.src = 'https://checkout.razorpay.com/v1/checkout.js'
    script.onload = () => resolve(true)
    script.onerror = () => resolve(false)
    document.body.appendChild(script)
  })
}

export default function CheckoutPage() {
  const { user } = useSelector((s: RootState) => s.auth)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const [plans, setPlans] = useState<SubscriptionPlan[]>([])
  const [selected, setSelected] = useState<SubscriptionPlan | null>(null)
  const [loading, setLoading] = useState(true)
  const [paying, setPaying] = useState(false)
  const [error, setError] = useState('')

  const [customer, setCustomer] = useState({
    full_name: user?.full_name || '',
    email: user?.email || '',
    phone: user?.phone || '',
  })
  const [customerErrors, setCustomerErrors] = useState<Record<string, string>>({})

  const [couponInput, setCouponInput] = useState('')
  const [couponApplying, setCouponApplying] = useState(false)
  const [couponError, setCouponError] = useState('')
  const [appliedCoupon, setAppliedCoupon] = useState<{
    code: string
    discount_type: string
    discount_value: number
    discount_amount: number
    final_amount: number
    description: string | null
  } | null>(null)

  useEffect(() => {
    setCustomer({
      full_name: user?.full_name || '',
      email: user?.email || '',
      phone: user?.phone || '',
    })
  }, [user?.full_name, user?.email, user?.phone])

  useEffect(() => {
    const next = `/dashboard/checkout${window.location.search || ''}`
    evaluatePurchaseAccess(user || null, { next }).then(result => {
      if (!result.allowed && result.redirectTo) navigate(result.redirectTo, { replace: true })
    })
  }, [navigate, user])

  useEffect(() => {
    const queryPlanSlug = (searchParams.get('planSlug') || '').trim()
    const queryPlanId = Number(searchParams.get('planId') || '')
    const storedPlanRef = getSelectedCheckoutPlanRef()
    const preferredPlanRef = queryPlanSlug
      || (Number.isFinite(queryPlanId) && queryPlanId > 0 ? String(queryPlanId) : '')
      || (storedPlanRef || '')

    subscriptionsApi.getPlans(true)
      .then(result => {
        setPlans(result)
        if (preferredPlanRef) {
          const preferredPlanIdNumeric = Number(preferredPlanRef)
          const plan = result.find(p => p.slug === preferredPlanRef)
            || (Number.isFinite(preferredPlanIdNumeric) && preferredPlanIdNumeric > 0
              ? result.find(p => p.id === preferredPlanIdNumeric)
              : null)
            || null
          setSelected(plan)
          if (plan) saveSelectedCheckoutPlan(plan.slug)
        }
      })
      .catch(() => {
        setError('Unable to load plans. Please try again.')
      })
      .finally(() => setLoading(false))
  }, [searchParams])

  useEffect(() => {
    if (!selected) return
    saveSelectedCheckoutPlan(selected.slug)
  }, [selected?.slug])

  useEffect(() => {
    setAppliedCoupon(null)
    setCouponInput('')
    setCouponError('')
  }, [selected?.id])

  // final_price is the plan's list price with its discount already applied by the API.
  const baseAmount = selected ? Number(selected.final_price) : 0
  const finalAmount = appliedCoupon ? appliedCoupon.final_amount : baseAmount
  const features = useMemo(() => (selected ? getPlanFeatureList(selected) : []), [selected])

  const handleApplyCoupon = async () => {
    if (!selected) {
      setCouponError('Select a plan first')
      return
    }
    if (!couponInput.trim()) return

    setCouponApplying(true)
    setCouponError('')
    try {
      const result = await couponsApi.validate(couponInput.trim(), selected.id, baseAmount)
      setAppliedCoupon(result)
      setCouponInput('')
    } catch (err: any) {
      setCouponError(err.response?.data?.detail || 'Invalid coupon code')
      setAppliedCoupon(null)
    } finally {
      setCouponApplying(false)
    }
  }

  const handleCancelCheckout = () => {
    clearSelectedCheckoutPlan()
    navigate('/dashboard/subscription')
  }

  const validateCustomerField = (field: keyof typeof customer, value: string) => {
    if (field === 'full_name') {
      if (!value.trim() || !validateFullName(value)) return 'Please enter a valid full name.'
      return ''
    }
    if (field === 'email') {
      if (!value.trim() || !validateEmail(value)) return 'Please enter a valid email address.'
      return ''
    }
    if (field === 'phone') {
      if (!value.trim() || !validatePhone(value)) return 'Please enter a valid 10-digit mobile number.'
      return ''
    }
    return ''
  }

  const handleCustomerFieldChange = (field: keyof typeof customer, value: string) => {
    const nextValue = field === 'phone' ? value.replace(/\D/g, '').slice(0, 10) : value
    setCustomer(p => ({ ...p, [field]: nextValue }))
    setCustomerErrors(prev => {
      const next = { ...prev }
      const fieldError = validateCustomerField(field, nextValue)
      if (fieldError) next[field] = fieldError
      else delete next[field]
      return next
    })
  }

  const handlePay = async () => {
    if (!selected) {
      setError('Please select a plan')
      return
    }

    const customerErrs: Record<string, string> = {}
    ;(['full_name', 'email', 'phone'] as (keyof typeof customer)[]).forEach(field => {
      const fieldError = validateCustomerField(field, customer[field])
      if (fieldError) customerErrs[field] = fieldError
    })
    if (Object.keys(customerErrs).length > 0) {
      setCustomerErrors(customerErrs)
      setError('Please correct the highlighted fields before continuing.')
      return
    }

    setError('')
    const basePaise = Math.round(baseAmount * 100)
    const finalPaise = Math.round(finalAmount * 100)

    const loaded = await loadRazorpayScript()
    if (!loaded) {
      setError('Failed to load payment gateway')
      return
    }

    setPaying(true)
    try {
      const order = await paymentsApi.createOrder(selected.id, basePaise, appliedCoupon?.code)

      const rzp = new window.Razorpay({
        key: order.razorpay_key,
        amount: order.amount,
        currency: order.currency,
        name: 'AnalyzerView',
        description: order.plan_name,
        order_id: order.order_id,
        prefill: {
          name: customer.full_name,
          email: customer.email,
          contact: customer.phone,
        },
        theme: { color: '#6366f1' },
        handler: async (response: any) => {
          try {
            await paymentsApi.verifyPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              plan_id: selected.id,
              amount: finalPaise,
              coupon_code: appliedCoupon?.code,
              discount_amount: appliedCoupon?.discount_amount,
            })
            clearSelectedCheckoutPlan()
            navigate('/dashboard/subscription?paid=1')
          } catch (err: any) {
            setError(err.response?.data?.detail || 'Payment verification failed')
          } finally {
            setPaying(false)
          }
        },
        modal: {
          ondismiss: () => setPaying(false),
        },
      })

      rzp.on('payment.failed', (resp: any) => {
        paymentsApi.markFailed(order.order_id, resp?.error?.description).catch(() => {})
        setError('Payment failed. No charge was made.')
        setPaying(false)
      })

      rzp.open()
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to create payment order')
      setPaying(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Checkout</h1>
        <p className="text-sm text-gray-400 mt-1">Review your selected plan and complete payment securely.</p>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-xl text-sm">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <aside className="lg:col-span-1">
          <div className="sticky top-6 bg-dark-800 border border-dark-600 rounded-2xl p-5 space-y-4">
            <h2 className="text-sm font-semibold tracking-wide uppercase text-gray-400">Selected Plan</h2>

            {selected ? (
              <>
                <div>
                  <h3 className="text-lg font-semibold text-white">{selected.name}</h3>
                  {selected.description ? (
                    <div className="mt-2 text-sm text-gray-400 [&_p]:mb-2 [&_p:last-child]:mb-0 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5">
                      <RichTextContent html={selected.description} />
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400 mt-1">Advisory subscription plan</p>
                  )}
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between text-gray-400">
                    <span>Price</span>
                    <span className="text-gray-200">
                      {Number(selected.savings) > 0 && (
                        <span className="text-gray-500 line-through mr-2">
                          ₹{Number(selected.price_min).toLocaleString('en-IN')}
                        </span>
                      )}
                      ₹{baseAmount.toLocaleString('en-IN')}
                    </span>
                  </div>
                  {Number(selected.savings) > 0 && (
                    <div className="flex justify-between text-green-400">
                      <span>Plan discount ({Math.round(Number(selected.discount_percent))}% off)</span>
                      <span>-₹{Number(selected.savings).toLocaleString('en-IN')}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-gray-400">
                    <span>Billing cycle</span>
                    <span className="text-gray-200">{getBillingCycleLabel(selected.duration_days)}</span>
                  </div>
                </div>

                <div>
                  <p className="text-xs text-gray-500 mb-2">Features</p>
                  {features.length > 0 ? (
                    <ul className="space-y-1.5">
                      {features.map(feature => (
                        <li key={feature} className="text-xs text-gray-300 flex items-start gap-2">
                          <CheckCircleIcon className="w-3.5 h-3.5 text-green-400 mt-0.5 flex-shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-xs text-gray-500">No feature details available.</p>
                  )}
                </div>

                <div className="border-t border-dark-600 pt-3">
                  <div className="flex justify-between font-semibold text-white">
                    <span>Total payable</span>
                    <span>₹{finalAmount.toLocaleString('en-IN')}</span>
                  </div>
                </div>
              </>
            ) : (
              <p className="text-sm text-gray-400">Select a plan to continue with checkout.</p>
            )}
          </div>
        </aside>

        <section className="lg:col-span-2 space-y-4">
          <div className="bg-dark-800 border border-dark-600 rounded-2xl p-5 space-y-4">
            <h2 className="text-base font-semibold text-white">Customer Details</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-400 mb-1">Name</label>
                <input
                  type="text"
                  value={customer.full_name}
                  onChange={e => handleCustomerFieldChange('full_name', e.target.value)}
                  onBlur={e => handleCustomerFieldChange('full_name', e.target.value)}
                  className={`w-full bg-dark-700 border text-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 ${customerErrors.full_name ? 'border-red-500 focus:ring-red-500' : 'border-dark-600 focus:ring-primary-500'}`}
                />
                {customerErrors.full_name && <p className="mt-1 text-xs text-red-400">{customerErrors.full_name}</p>}
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Email</label>
                <input
                  type="email"
                  value={customer.email}
                  onChange={e => handleCustomerFieldChange('email', e.target.value)}
                  onBlur={e => handleCustomerFieldChange('email', e.target.value)}
                  className={`w-full bg-dark-700 border text-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 ${customerErrors.email ? 'border-red-500 focus:ring-red-500' : 'border-dark-600 focus:ring-primary-500'}`}
                />
                {customerErrors.email && <p className="mt-1 text-xs text-red-400">{customerErrors.email}</p>}
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs text-gray-400 mb-1">Phone</label>
                <input
                  type="tel"
                  value={customer.phone}
                  onChange={e => handleCustomerFieldChange('phone', e.target.value)}
                  onBlur={e => handleCustomerFieldChange('phone', e.target.value)}
                  maxLength={10}
                  className={`w-full bg-dark-700 border text-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 ${customerErrors.phone ? 'border-red-500 focus:ring-red-500' : 'border-dark-600 focus:ring-primary-500'}`}
                />
                {customerErrors.phone && <p className="mt-1 text-xs text-red-400">{customerErrors.phone}</p>}
              </div>
            </div>
          </div>

          <div className="bg-dark-800 border border-dark-600 rounded-2xl p-5 space-y-4">
            <h2 className="text-base font-semibold text-white">Plan & Payment</h2>

            <div>
              <label className="block text-xs text-gray-400 mb-1">Choose Plan</label>
              <select
                value={selected?.id ?? ''}
                onChange={e => {
                  const nextId = Number(e.target.value)
                  const next = plans.find(p => p.id === nextId) || null
                  setSelected(next)
                }}
                className="w-full bg-dark-700 border border-dark-600 text-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
              >
                <option value="" disabled>Select a plan</option>
                {plans.map(plan => (
                  <option key={plan.id} value={plan.id}>
                    {plan.name} - ₹{Number(plan.final_price).toLocaleString('en-IN')}
                  </option>
                ))}
              </select>
            </div>

            <div className="border border-dark-600 rounded-lg p-4 space-y-3">
              <p className="text-sm font-medium text-gray-300">Coupon code</p>
              {appliedCoupon ? (
                <div className="flex items-center justify-between bg-green-900/20 border border-green-500/30 rounded-md px-3 py-2">
                  <div>
                    <span className="text-green-400 font-semibold text-sm">{appliedCoupon.code}</span>
                    {appliedCoupon.description && (
                      <span className="text-gray-400 text-xs ml-2">- {appliedCoupon.description}</span>
                    )}
                    <p className="text-green-300 text-xs mt-0.5">Saving ₹{appliedCoupon.discount_amount.toLocaleString('en-IN')}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => { setAppliedCoupon(null); setCouponError('') }}
                    className="text-xs text-gray-500 hover:text-gray-300"
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={couponInput}
                    onChange={e => { setCouponInput(e.target.value.toUpperCase()); setCouponError('') }}
                    placeholder="Enter code"
                    className="flex-1 bg-dark-700 border border-dark-600 text-gray-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-primary-500"
                  />
                  <Button type="button" variant="outline" onClick={handleApplyCoupon} disabled={couponApplying || !couponInput.trim()}>
                    {couponApplying ? 'Applying...' : 'Apply'}
                  </Button>
                </div>
              )}
              {couponError && <p className="text-xs text-red-400">{couponError}</p>}
            </div>

            <div className="bg-dark-700 border border-dark-600 rounded-lg p-4 text-sm space-y-2">
              <div className="flex justify-between text-gray-400">
                <span>Payment method</span>
                <span className="text-gray-200">Razorpay (UPI/Card/NetBanking)</span>
              </div>
              <div className="flex justify-between text-gray-400">
                <span>Subtotal</span>
                <span>₹{baseAmount.toLocaleString('en-IN')}</span>
              </div>
              {appliedCoupon && (
                <div className="flex justify-between text-green-400">
                  <span>Discount ({appliedCoupon.code})</span>
                  <span>-₹{appliedCoupon.discount_amount.toLocaleString('en-IN')}</span>
                </div>
              )}
              <div className="flex justify-between font-semibold text-white border-t border-dark-600 pt-2">
                <span>Total payable</span>
                <span>₹{finalAmount.toLocaleString('en-IN')}</span>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" onClick={handleCancelCheckout}>Cancel</Button>
              <Button type="button" className="flex-1" loading={paying} disabled={!selected} onClick={handlePay}>
                {selected ? `Pay ₹${finalAmount.toLocaleString('en-IN')}` : 'Select a plan'}
              </Button>
            </div>
          </div>
        </section>
      </div>
    </div>
  )
}
